<template>
  <section
    v-if="currentVariant"
    class="product-shopblock"
    :class="{ '-blacktheme': blackTheme, '-no-header': hideHeader }"
  >
    <div class="product-section-grid grid">
      <!-- IMAGE SLIDER -->
      <section class="product-shopblock-image g-12 g-md-6 g-lg-8 -p-relative">
        <ProductTopSlider
          :variant="currentVariant"
          :allVariants="variants"
          :gender="model.Gender"
          :nativeSlider="$mqAliases.xs || $mqAliases.sm"
          :productName="model.Name"
          :showControls="true"
          :showPackShotToggler="!outlet && $mqAliases.desktop"
          :fullScreen="true"
          :pageType="'productPage'"
          :imageSettings="imageSettings"
        />
      </section>
      <div class="product-shopblock-keyinfo g-12 g-md-6 g-lg-4">
        <!-- NAME -->
        <h1 class="productname m-b-0 font-responsive-large">
          <span
            class="d-block font-responsive-small -small-caps"
            v-html="insertString('Global', genderKey)"
          />
          <span class="productname-concept -bold" v-html="model.Concept"
          />&nbsp;<span class="productname-name -thin" v-html="productName" />
        </h1>

        <!-- PRICE ROW -->
        <section
          class="product-shopblock-price-area font-responsive-small m-t-9 m-t-lg-18"
          style="min-height: 12px"
        >
          <div class="no-font-spacing m-b-12">
            <div v-if="outletAccess" class="d-flex align-items-end">
              <div>
                <span
                  >{{ currentVariantPrice.currencyCode }}
                  {{
                    !archived
                      ? currentVariantPrice.price
                      : currentVariantPrice.fullPrice
                  }}</span
                >
                <span
                  v-if="currentVariantPrice.hasDiscount && !archived"
                  class="font-responsive-small -linethrough m-l-6"
                  >{{ currentVariantPrice.fullPrice }}</span
                >
              </div>
              <InfoToolTip
                v-if="currentVariantPrice.hasDiscount && !archived"
                :local="true"
                :reverse="false"
                :toolTipZIndex="'100000'"
                :type="'exclamation'"
                :text="insertString('Global', 'OutletFullPriceInfoText')"
                style="position: relative"
              />
            </div>
          </div>

          <div class="d-flex flex-wrap align-items-center">
            <div
              class="bv-rating font-responsive-small m-r-18  m-b-12"
              v-if="model.BvRating"
            >
              <span class="no-font-spacing">
                <span>{{ Math.round(model.BvRating * 10) / 10 }}</span>
                <SvgComponent
                  :icon="{
                    name: 'Star',
                    size: 12,
                    color: Globals.colors.primary,
                  }"
                />
                <a
                  v-scroll-to="'#pageTab-ProductReviews'"
                  class="-blue -clickable m-l-3"
                  href="#pageTab-ProductReviews"
                  >{{ insertString("Product", "ShowReviews") }}</a
                >
              </span>
            </div>
            <WishlistButton
            class="m-b-12"
              v-if="outletAccess && !archived"
              :active="hasSizeOnWishlist"
              @on-clicked="openWishlistModal"
            />
          </div>
        </section>
        <!-- OVERLAYS FOR SIZE AND COLOR -->
        <div
          v-if="!hideColorAndSize"
          class="-devider m-t-lg-15 m-b-12"
        />

        <ShopBlockVariantAndSize
          ref="VariantAndSize"
          :variants="variants"
          :gender="model.Gender"
          :colorIndex="colorVariantIndex"
          :fit="model.Fit"
          :archived="archived"
          :outlet="outlet"
          :availibleInFuture="(availibleInFuture && !outlet) || marketBlocked"
          :sizeGuideLink="sizeGuideLink"
          :hidden="hideColorAndSize"
          :largeThumbs="model.Category === 'Tent'"
          @color-index-change="colorVariantIndex = $event"
          @size-change="sizeVariantIndex = $event"
        />

        <div class="-devider m-t-15 m-b-6 m-b-lg-9" v-if="outletAccess" />

        <ShopBlockCallToActions
          v-if="outletAccess && !marketBlocked"
          :model="model"
          :currentVariant="currentVariant"
          :sizeVariantIndex="sizeVariantIndex"
          :selectedSizeObject="selectedSizeObject"
          :availibleInFuture="availibleInFuture"
          :outlet="outlet"
          :archived="archived"
          :oneSize="currentVariantSizes.length === 1"
          :configurationProducts="configurationProducts"
          :hideColorAndSize="hideColorAndSize"
        />
        <Alert
          v-else-if="marketBlocked"
          :text="insertString('Product', 'NotAvailibleInMarket')"
          :icon="true"
          :small="true"
          :type="'warning'"
        />
        <Alert v-if="!marketBlocked && havePfas && isShippingUs"
          :text="insertString('Product', 'PfasUsShippingInfo')"
          :icon="true"
          :small="true"
          :type="'info'"
        />
        <div
          v-if="shippingMessage || !hideColorAndSize"
          class="-devider m-t-18"
        />
        <ShippingInfo
          class="m-b-18"
          v-if="shippingMessage"
          :message="shippingMessage"
        />
        <USPPoster v-if="!hideColorAndSize" :oneString="true" />
        <div class="-devider" />

        <div class="product-shopblock-description">
          <div>
            <h2 class="-visual-hidden">Description</h2>
            <p class="m-b-12">{{ ingress }}</p>

            <Expander
              v-if="description"
              class="m-b-18"
              label="description"
              :content="description"
              :minimal="true"
              icon="chevron-down"
              :collapseText="insertString('ProductPage', 'ReadLess')"
              >{{ insertString("ProductPage", "ReadMore") }}</Expander
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import productComputedMixin from '@/Scripts/lib/mixins/productComputedMixin.js';

import { TrackerProductObject, TrackerProductViewed, TrackerSizeGuideClicked } from '@/Scripts/lib/helper/trackingHelper.js';
import { eventBus } from '@/Scripts/lib/helper/eventBus.js';
import { MARKETS } from '@/Scripts/constants';
import ScrollTo from 'vue-scrollto';
import themeHelper from '@/Scripts/lib/helper/themeHelper.js';
import siteInfo from '@/Scripts/lib/helper/siteInfo.js';
import shippingMessageMixin from '@/Scripts/lib/mixins/shippingMessageMixin.js';
import { ProductIsAvailableInFuture } from '@/Scripts/lib/helper/dateHelper.js';
import imgSrcMixin from '@/Scripts/lib/mixins/imgSrcMixin';
import toastMixin from '@/Scripts/lib/mixins/toastMixin.js';
import Expander from '@/Scripts/components/shared/Expander/Expander.vue';
import USPPoster from '@/Scripts/components/shared/USPPoster/USPPoster.vue';
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';
import ProductTopSlider from '@/Scripts/components/pages/ProductV2/Blocks/ShopBlockAssets/ProductTopSlider.vue';
import ShopBlockVariantAndSize from '@/Scripts/components/pages/ProductV2/Blocks/ShopBlockAssets/ShopBlockVariantAndSize.vue';
import ShopBlockCallToActions from '@/Scripts/components/pages/ProductV2/Blocks/ShopBlockAssets/ShopBlockCallToActions.vue';
const InfoToolTip = () => import('@/Scripts/components/shared/InfoToolTip/InfoToolTip.vue');
const ShippingInfo = () => import('@/Scripts/components/shared/ShippingInfo/ShippingInfo.vue');
const WishlistButton = () => import('@/Scripts/components/productcards/ProductCard/Assets/WishlistButton.vue');
const Alert = () => import(/* webpackChunkName: "Alert" */'@/Scripts/components/shared/Alert/Alert.vue');

export default {
  mixins: [productComputedMixin, imgSrcMixin, siteInfo, toastMixin, themeHelper, shippingMessageMixin],
  directives: {
    ScrollTo
  },
  components: {
    InfoToolTip,
    ProductTopSlider,
    ShopBlockVariantAndSize,
    ShopBlockCallToActions,
    WishlistButton,
    SvgComponent,
    Expander,
    ShippingInfo,
    USPPoster,
    Alert

  },
  props: {
    model: Object,
    outlet: Boolean,
    archived: Boolean,
    ingress: String,
    description: String,
    configurationProducts: Array,
    variantInUrl: String,
    unisexWomanMode: Boolean,
    sizeGuideLink: Boolean,
    hideColorAndSize: Boolean
  },
  data () {
    return {
      colorVariantIndex: 0,
      sizeVariantIndex: -1

    };
  },
  watch: {
    colorVariantIndex () {
      this.$emit('color-code-change', this.currentVariant.ColorCode);
    },
    sizeVariantIndex (newval, oldval) {
      // if (this.isLoggedIn && oldval === -1 && !this.wishlistItems.length) {
      //   if (document.body.getAttribute('data-identify') !== '') {
      //     this.$store.dispatch('bag/getWishlist', document.body.getAttribute('data-identify'));
      //   }
      // }
    }
  },
  computed: {
    /* */
    /* imported from productComputedMixin: */
    /* productName,variants, currentVariant, currentVariantPrice, selectedSizeObject */
    /* */
    ...mapState({
      PackshotsFirst: state => state.siteInfo.features.PackshotsFirst,
      features: state => state.siteInfo.features,
      isLoyalty: state => state.appContext.isLoyalty,
      isLoggedIn: state => state.appContext.isLoggedIn,
      blackTheme: state => state.appContext.blackTheme,
      hideHeader: state => state.appContext.hideHeader,
      wishlistItems: state => state.bag.wishlistItems
    }),
    imageSettings () {
      const padding = this.model && this.model.Category === 'Tent' ? 3 : null;
      return {
        initWidth: 360,
        proportion: 1,
        bgColor: '#F5F5F5',
        packShotPaddingOverride: padding
      };
    },
    genderKey () {
      return {
        Male: 'Mens',
        Female: 'Womens',
        Unisex: 'Unisex',
        Junior: 'Junior'
      }[this.model.Gender];
    },
    backgroundColor () {
      if (this.blackTheme) {
        return '000000';
      }
      return this.Globals.colors.grayLighter;
    },
    outletAccess () {
      return !this.outlet || this.isLoyalty;
    },
    availibleInFuture () {
      if (!this.model.AvailableFrom) {
        return false;
      }
      return ProductIsAvailableInFuture(this.model.AvailableFrom);
    },
    currentMarket () {
      const currentCode = document.body.getAttribute('data-country-code');
      const market = MARKETS.find(x => x.code === currentCode);
      return market ? market.name : '';
    },
    marketBlocked () {
      if (this.currentMarket && this.model.MarketFilter && this.model.MarketFilter.length) {
        if (this.model.MarketFilter.includes(this.currentMarket)) {
          return true;
        }
      }
      return false;
    },
    trackerObject () {
      return TrackerProductObject({
        product: this.model,
        colorVariant: this.currentVariant,
        size: this.selectedSizeObject ? this.selectedSizeObject.Size : '',
        outlet: this.outlet,
        pageType: this.$root._vnode.tag.replace(/vue-component-\d*-/, '').trim()
      });
    },
    isShippingUs () {
      if (document.body.getAttribute('data-country-code') === 'US') {
        return true;
      }
      return false;
    },
    havePfas () {
      return this.model.HavePfas;
    }
  },
  methods: {
    updateSelectedVariant (index) {
      this.$refs.VariantAndSize.sizeChange(index);
    },
    setColorIndexOnLoad () {
      const color = this.variantInUrl ? this.variantInUrl : this.model.MainColor;
      const index = this.variants.findIndex(v => v.ColorCode === color);
      this.colorVariantIndex = index > -1 ? index : 0;
    }
  },
  mounted () {
    this.setColorIndexOnLoad();

    eventBus.$on('openPageSection', data => {
      if (data && data.state === 'ProductFitSize' && data.state === 'size') {
        TrackerSizeGuideClicked(this.trackerObject);
      }
    });
    eventBus.$on('WISHLIST_SIZE_CHANGED', data => {
      if (data && data.code === this.model.Code && data.colorCode === this.currentVariant.ColorCode) {
        this.$refs.VariantAndSize.sizeChange(data.sizeIndex);
      }
    });
    this.getShippingMsg([this.model]);
    this.$nextTick(() => {
      this.$emit('ready');
      const obj = this.trackerObject;
      window.addEventListener('CookieInformationConsentGiven', function (event) {
        TrackerProductViewed(obj);
      });
    });

    if (this.isLoggedIn && !this.wishlistItems.length) {
      if (document.body.getAttribute('data-identify') !== '') {
        this.$store.dispatch('bag/getWishlist', document.body.getAttribute('data-identify'));
      }
    }
  }

};
</script>

<style lang="scss">
@import "@/scss/defaults.scss";

.product-shopblock {
  background-color: $white;
  padding-top: 0;
  border-bottom-width: 0;
  .productname {
    line-height: rem(30px);
  }
  .product-price {
    font-weight: 400;
  }
  &-price-area {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    .no-font-spacing {
      @include font-spacing-responsive(small);
    }
  }
  .bv-rating {
    display: flex;
    align-items: center;
    margin-bottom: 1px;
    svg {
      margin-bottom: 1px;
    }
  }

  &.-blacktheme {
    background-color: #000000;
    color: $white;
    margin-bottom: 0 !important;
    .expander-link,
    .-blue {
      color: $blue-light;
    }
  }
  .-small-grid-gap {
    grid-column-gap: 18px !important;
  }

  .-devider {
    margin: 15px 0;
    border-bottom: 1px solid $gray-light;
    // @include media-breakpoint-up(md) {
    //   margin: 18px 0;
    // }
  }

  @include media-breakpoint-up(md) {
    // margin-bottom: 36px !important;
    padding-bottom: 0 !important;
  }

  .product-section-content {
    padding-bottom: 0;
    padding-top: 0;
  }

  .-text-helper {
    @include font-responsive(smallest);
  }
  &-image {
    @include media-breakpoint-down(sm) {
      position: sticky;
      top: 0;
      width: 100vw !important;
      max-width: 100vw !important;
      padding: 0 !important;
      margin: 0 -18px;
      transition: top 0.25s;
      top: 60px;
      .-no-header & {
        top: 0;
      }
    }
  }

  &-keyinfo {
    @include media-breakpoint-down(sm) {
      position: relative;
      z-index: 10;
      background-color: $white;
      width: 100vw !important;
      max-width: 100vw !important;
      padding: 15px 18px 0 18px !important;
      margin: 0 -18px;
      // opacity: 0.97;
    }
    .product-name {
      font-size: 18px;
      line-height: 24px;

      @include media-breakpoint-up(md) {
        font-size: 24px;
        line-height: 30px;
      }
    }
  }
  &-price {
    @include font-responsive(small);
    // font-weight: 500;
  }
  &-actions {
    @include media-breakpoint-up(md) {
      margin-bottom: 18px;
    }
  }
  &-description {
    margin-bottom: 18px;
    @include font-responsive(body);
    @include media-breakpoint-up(md) {
      margin-bottom: 0;
    }
  }
}
</style>
